import { jsx, jsxs } from 'react/jsx-runtime';
import { isPresetGradientColor } from '@flex-design-system/fx-colors';
import { isNil } from '@flex-design-system/fx-utils';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { getColor } from '../../../../../utils/colors.mjs';
import { getSvgUrlId } from '../../../../../utils/svg.mjs';
import FlexLinearGradient, { getID } from '../../../../linear-gradient/FlexLinearGradient.mjs';
import { useAvatarContext } from '../../context.mjs';
import { avatarOutline, avatarOutlineStrokeWidth, avatarOutlineSvgPath, avatarOutlineSvgSize } from './outline.vanilla.mjs';

const AvatarOutline = forwardRef(
  ({
    className: classNameFromProps,
    color = "white",
    strokeWidth = 2,
    style: styleFromProps,
    half = false,
    ...props
  }, forwardedRef) => {
    const linearGradientId = useId();
    const { avatarRootElement, size } = useAvatarContext("Avatar.Outline");
    const outlinePortalContainer = avatarRootElement?.querySelector(
      '[data-scope="avatar"][data-part="outline-portal"]'
    );
    if (isNil(outlinePortalContainer)) {
      return null;
    }
    const className = classNames(avatarOutline, classNameFromProps);
    const style = Object.assign(
      {},
      assignInlineVars({
        [avatarOutlineStrokeWidth]: `${strokeWidth}px`
      }),
      styleFromProps
    );
    const isPresetGradientOutlineColor = isPresetGradientColor(color);
    const outlineColor = isPresetGradientOutlineColor ? getSvgUrlId(getID(color, linearGradientId)) : getColor(color);
    return createPortal(
      /* @__PURE__ */ jsx(
        "div",
        {
          "data-scope": "avatar",
          "data-part": "outline",
          className,
          style,
          ref: forwardedRef,
          ...props,
          children: /* @__PURE__ */ jsxs(
            "svg",
            {
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg",
              preserveAspectRatio: "none",
              ...getSvgSize({ size, strokeWidth }),
              children: [
                isPresetGradientOutlineColor && /* @__PURE__ */ jsx(FlexLinearGradient, { id: linearGradientId, type: color }),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    style: assignInlineVars({
                      [avatarOutlineSvgSize]: `${size + strokeWidth * 2}`
                    }),
                    className: avatarOutlineSvgPath,
                    d: half ? "M18 1C14.6832 1 11.9496 1.1764 9.7244 1.6709 C 7.4879 2.168 5.7042 2.9996 4.3519 4.3519 C 2.9996 5.7042 2.168 7.4879 1.6709 9.7244 C 1.1764 11.9496 1 14.6832 1 18 H 35 C 35 18 35 18 35 18 C 35 14.6832 34.8236 11.9496 34.3291 9.7244 C 33.832 7.4879 33.0004 5.7042 31.6481 4.3519 C 30.2958 2.9996 28.5121 2.168 26.2756 1.6709 C 24.0504 1.1764 21.3168 1 18 1 Z" : "M18 1C14.6832 1 11.9496 1.17642 9.72443 1.67094C7.48791 2.16799 5.70419 2.9996 4.35189 4.35189C2.9996 5.70419 2.16799 7.48791 1.67094 9.72443C1.17642 11.9496 1 14.6832 1 18C1 21.3168 1.17642 24.0504 1.67094 26.2756C2.16799 28.5121 2.9996 30.2958 4.35189 31.6481C5.70419 33.0004 7.48791 33.832 9.72443 34.3291C11.9496 34.8236 14.6832 35 18 35C21.3168 35 24.0504 34.8236 26.2756 34.3291C28.5121 33.832 30.2958 33.0004 31.6481 31.6481C33.0004 30.2958 33.832 28.5121 34.3291 26.2756C34.8236 24.0504 35 21.3168 35 18C35 14.6832 34.8236 11.9496 34.3291 9.72443C33.832 7.48791 33.0004 5.70419 31.6481 4.35189C30.2958 2.9996 28.5121 2.16799 26.2756 1.67094C24.0504 1.17642 21.3168 1 18 1Z",
                    fill: outlineColor
                  }
                )
              ]
            }
          )
        }
      ),
      outlinePortalContainer
    );
  }
);
function getSvgSize(params) {
  const { size, strokeWidth } = params;
  const svgSize = size + strokeWidth * 2;
  return {
    width: svgSize,
    height: svgSize,
    viewBox: [0, 0, svgSize, svgSize].join(" ")
  };
}
AvatarOutline.displayName = "Avatar.Outline";

export { AvatarOutline };
//# sourceMappingURL=Outline.mjs.map
